'use client';

import type { ClassValue } from 'clsx';

import { useAtomSet, useAtomValue } from '@effect/atom-react';
import {
  AlphabetRail,
  AlphabetRailItem,
  AlphabetRailItemVariant,
  AlphabetRailLetter,
  AlphabetRailLetterPlaceholder,
  AlphabetRailLetterState,
} from '@vs-shop/commerce-ui/alphabetRail';
import { manipulateUrlSearchParams } from '@vs-shop/domain/string/urlSearchParams';
import { clsxm } from '@vs-shop/utils';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useIntl } from 'react-intl';

import {
  alphabetFilterParamName,
  glossaryNameFilterParamName,
} from '@vs-shop/schemas/config/urlParams';

import { getGlossaryAlphabetAtom } from '../../../../effect/atom/glossary/getGlossaryAlphabet';
import { currentAlphabetFilterAtom } from '../../../../effect/atom/glossary/glossaryFilters';
import { glossaryFilterActionFn } from '../../../../effect/atom/glossary/glossarySearchInput';
import {
  buildGlossaryAlphabetTiles,
  sameAlphabetValue,
} from '../../../../effect/data/glossary/glossaryAlphabetTiles';

type Props = {
  canonicalUrl: string;
  className?: ClassValue;
};

/**
 * How this page's rail is sized.
 *
 * The shared rail states no box (see its own note): its two callers in this
 * repo are two shipped pages at two sizes. These are `/glosar`'s, unchanged —
 * a tile is 44x44 at its smallest, because it is a touch target and the grid
 * packs three dozen of them edge to edge.
 */
const glossaryAlphabetItemClasses = 'max-w-11 min-w-11';

const glossaryAlphabetLetterClasses =
  'h-11 focus-visible:ring-primary focus-visible:ring-2 focus-visible:outline-none';

const glossaryAlphabetUnavailableClasses = `${glossaryAlphabetLetterClasses} cursor-default`;

/**
 * The glossary's rail over `@vs-shop/commerce-ui/alphabetRail`.
 *
 * Which tiles exist is this tenant's answer and stays here: the grid is widened
 * by whatever initials the catalogue actually holds, so a term under `Č` is
 * reachable rather than stranded outside `a–z`, and the endpoint's casing —
 * which is not part of its contract — is folded by the lookup rather than by
 * the rail.
 */
export function GlossarySearchAlphabet({ canonicalUrl, className }: Props) {
  const intl = useIntl();
  const alphabetResponse = useAtomValue(getGlossaryAlphabetAtom);
  const currentAlphabet = useAtomValue(currentAlphabetFilterAtom);

  const dispatchFilter = useAtomSet(glossaryFilterActionFn, { mode: 'promise' });

  const reportedInitials = AsyncResult.match(alphabetResponse, {
    onInitial: () => [] as readonly string[],
    onFailure: () => [] as readonly string[],
    onSuccess: (success) => success.value.alphabet,
  });

  const tiles = buildGlossaryAlphabetTiles(reportedInitials);

  const initialsWithTerms = new Set(reportedInitials.map((initial) => initial.toLowerCase()));

  const selectLetter = (letter: string) => {
    void dispatchFilter(
      currentAlphabet !== null && sameAlphabetValue(letter, currentAlphabet)
        ? { _tag: 'Clear' }
        : { _tag: 'SelectLetter', letter }
    );
  };

  return (
    <AlphabetRail
      aria-label={intl.formatMessage({
        id: 'component.client.page.glossary.alphabet.label',
        defaultMessage: 'Rejstřík podle písmen',
      })}
      className={clsxm('w-full', className)}
    >
      {tiles.map((letter) => {
        const hasLetterOccurrence = initialsWithTerms.has(letter.toLowerCase());
        const isSelected = currentAlphabet !== null && sameAlphabetValue(letter, currentAlphabet);

        return (
          <AlphabetRailItem
            key={letter}
            variant={AlphabetRailItemVariant.LETTER}
            className={glossaryAlphabetItemClasses}
          >
            {hasLetterOccurrence ? (
              <AlphabetRailLetter
                href={manipulateUrlSearchParams(canonicalUrl, {
                  [alphabetFilterParamName]: letter,
                  [glossaryNameFilterParamName]: null,
                })}
                state={
                  isSelected ? AlphabetRailLetterState.SELECTED : AlphabetRailLetterState.AVAILABLE
                }
                aria-current={isSelected ? 'true' : undefined}
                className={glossaryAlphabetLetterClasses}
                onClick={(e) => {
                  e.preventDefault();
                  selectLetter(letter);
                }}
              >
                {letter}
              </AlphabetRailLetter>
            ) : (
              <AlphabetRailLetterPlaceholder className={glossaryAlphabetUnavailableClasses}>
                {letter}
              </AlphabetRailLetterPlaceholder>
            )}
          </AlphabetRailItem>
        );
      })}
    </AlphabetRail>
  );
}
